import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Info } from "lucide-react";
import { commissionsApi } from "@/api/commissions";
import { ApiError } from "@/api/client";
import { commissionKeys } from "@/hooks/commissions/commissionKeys";
import { useAuth } from "@/hooks/useAuth";
import { Button, Card, FormField, Input } from "@/design-system/components/ui";
import { currency, percent } from "@/components/commissions/formatters";
import { commissionAmount } from "@/components/commissions/commissionMath";
import { ClaimStatusBadge } from "@/components/commissions/ClaimStatusBadge";

export function ClaimEntryPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const { id } = useParams<{ id: string }>();
  const editing = !!id;
  // The route admits both audiences now, so the page has to tell them apart:
  // the draft save/submit pair runs on updateClaim, which is ADMIN-gated.
  const canManualCreate = hasPermission("Commission.ManualCreate");

  const existing = useQuery({
    queryKey: commissionKeys.claim(id ?? ""),
    queryFn: () => commissionsApi.getClaim(id!),
    enabled: editing,
  });

  const pools = useQuery({
    queryKey: commissionKeys.poolList("active"),
    queryFn: () => commissionsApi.listPools(false),
  });
  const templates = useQuery({
    queryKey: commissionKeys.templatesMine(),
    queryFn: () => commissionsApi.listTemplates(),
  });

  const [poolId, setPoolId] = useState("");
  const [loanReference, setLoanReference] = useState("");
  const [description, setDescription] = useState("");
  const [grossRevenue, setGrossRevenue] = useState<string>("");
  const [commissionPercent, setCommissionPercent] = useState<string>("");
  const [revisionSummary, setRevisionSummary] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (existing.data) {
      const s = existing.data.summary;
      setPoolId(s.revenuePoolId);
      setLoanReference(s.loanReference ?? "");
      setDescription(s.serviceDescription);
      setGrossRevenue(String(s.grossRevenue));
      setCommissionPercent(String(s.commissionPercent));
    }
  }, [existing.data]);

  const selectedPool = useMemo(
    () => pools.data?.find((p) => p.id === poolId),
    [pools.data, poolId],
  );

  const grossNum = Number(grossRevenue) || 0;
  const pctNum = Number(commissionPercent) || 0;
  const computedAmount = commissionAmount(grossNum, pctNum);

  const similar = useQuery({
    queryKey: commissionKeys.similar(poolId),
    queryFn: () => commissionsApi.similar(poolId, null, 5),
    enabled: poolId.length > 0,
  });

  // A contested claim is answered through `revise` (Claims.Revise), NOT
  // `updateClaim` — that one is gated Commission.ManualCreate, which under the
  // lockdown is ADMIN-only, so a claimant reaching this form would 403 on save.
  // ReviseAsync also performs the Contested → UnderReview transition itself,
  // which is why the contested path submits in one action instead of the
  // save-then-submit pair (SubmitAsync accepts Draft only).
  const revising = editing && existing.data?.summary.status === "Contested";

  // The payload deliberately omits `loanId`. Under the FIND-12 clear contract
  // an absent loanId means "leave it alone"; sending `loanId: null` — which
  // this form used to do unconditionally — is what UNLINKED the claim from its
  // loan on every save. This form has no loan picker (the reference below is
  // free text), so it has no business changing the link at all.
  //
  // `loanReference` IS edited here, and the two paths want different things
  // from a blank field. On an EDIT, "" is the clear — the contract's null means
  // unchanged, so sending null would make a reference impossible to remove. On
  // a CREATE there is nothing to clear and no contract in play: CreateAsync
  // stores the value verbatim, so "" would persist an empty string where every
  // other unset reference in the table is null.
  const buildPayload = (mode: "create" | "edit") => ({
    loanReference: mode === "edit" ? loanReference.trim() : loanReference.trim() || null,
    revenuePoolId: poolId,
    serviceDescription: description.trim(),
    grossRevenue: grossNum,
    commissionPercent: pctNum,
  });

  const save = useMutation({
    mutationFn: async () => {
      if (revising) {
        return commissionsApi.revise(id!, {
          ...buildPayload("edit"),
          revisionSummary: revisionSummary.trim(),
        });
      }
      if (editing) {
        return commissionsApi.updateClaim(id!, {
          ...buildPayload("edit"),
          revisionSummary: revisionSummary.trim() || null,
        });
      }
      return commissionsApi.createClaim(buildPayload("create"));
    },
    onSuccess: (claim) => {
      qc.invalidateQueries({ queryKey: commissionKeys.all });
      navigate(`/commissions/${claim.summary.id}`);
    },
    onError: (e) => setError(e instanceof ApiError ? (e.errors[0] ?? e.message) : String(e)),
  });

  const submitAfterSave = useMutation({
    mutationFn: async () => {
      const draft = editing
        ? await commissionsApi.updateClaim(id!, {
            ...buildPayload("edit"),
            revisionSummary: null,
          })
        : await commissionsApi.createClaim(buildPayload("create"));
      return commissionsApi.submit(draft.summary.id);
    },
    onSuccess: (claim) => {
      qc.invalidateQueries({ queryKey: commissionKeys.all });
      navigate(`/commissions/${claim.summary.id}`);
    },
    onError: (e) => setError(e instanceof ApiError ? (e.errors[0] ?? e.message) : String(e)),
  });

  const applyTemplate = (tid: string) => {
    const t = templates.data?.find((x) => x.id === tid);
    if (!t) return;
    setPoolId(t.revenuePoolId);
    if (t.defaultDescription) setDescription(t.defaultDescription);
    if (t.defaultCommissionPercent !== null && t.defaultCommissionPercent !== undefined) {
      setCommissionPercent(String(t.defaultCommissionPercent));
    }
  };

  const disabled = !poolId || description.trim().length < 10 || grossNum <= 0 || pctNum <= 0 || pctNum > 100;
  // ReviseAsync requires a revision summary server-side; disable rather than
  // let the caller round-trip into a 400 they can see coming.
  const reviseDisabled = disabled || revisionSummary.trim().length === 0;

  return (
    <div className="flex flex-col gap-space-5 max-w-[1100px]">
      <div>
        <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-2">
          Commissions
        </div>
        <h1 className="font-display font-extrabold text-h2 uppercase tracking-wider text-onyx m-0">
          {editing ? "Edit claim" : "New claim"}
        </h1>
      </div>

      {error && (
        <div role="alert" className="flex items-start gap-space-2 p-space-3 border border-status-error rounded-md bg-status-error/5 text-status-error text-small">
          <AlertTriangle size={16} strokeWidth={1.5} aria-hidden />
          <span>{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-space-4">
        <Card className="flex flex-col gap-space-4">
          {templates.data && templates.data.length > 0 && !editing && (
            <FormField label="Start from a template">
              <select
                onChange={(e) => e.target.value && applyTemplate(e.target.value)}
                className="w-full h-10 px-space-3 border border-border-light rounded-md font-body text-small bg-white-pure"
                defaultValue=""
              >
                <option value="">No template</option>
                {templates.data.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} — {t.poolName}
                  </option>
                ))}
              </select>
            </FormField>
          )}

          <FormField label="Loan / Deal reference" helperText="Free-text for now; loan lookup will land in P1.">
            <Input
              value={loanReference}
              onChange={(e) => setLoanReference(e.target.value)}
              placeholder="e.g., LN-2026-0142 or borrower name"
            />
          </FormField>

          <FormField label="Revenue pool" required>
            <select
              value={poolId}
              onChange={(e) => setPoolId(e.target.value)}
              className="w-full h-10 px-space-3 border border-border-light rounded-md font-body text-small bg-white-pure"
            >
              <option value="">Select a pool…</option>
              {pools.data?.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code} — {p.name}
                </option>
              ))}
            </select>
          </FormField>

          {selectedPool && (
            <div className="flex flex-col gap-space-2">
              {selectedPool.description && (
                <div className="flex gap-space-2 p-space-3 bg-white-warm border border-border-light rounded-md text-small text-gray-mid">
                  <Info size={16} strokeWidth={1.5} className="text-gold-shadow shrink-0 mt-[2px]" aria-hidden />
                  <div>{selectedPool.description}</div>
                </div>
              )}
              {selectedPool.exclusions && (
                <div className="flex gap-space-2 p-space-3 bg-status-warning/5 border border-status-warning rounded-md text-small">
                  <AlertTriangle
                    size={16}
                    strokeWidth={1.5}
                    className="text-status-warning shrink-0 mt-[2px]"
                    aria-hidden
                  />
                  <div>
                    <div className="font-display font-semibold uppercase text-label tracking-label text-status-warning">
                      Exclusions
                    </div>
                    <div className="text-onyx mt-space-1">{selectedPool.exclusions}</div>
                  </div>
                </div>
              )}
              {selectedPool.suggestedDescriptions.filter((s) => s.isActive).length > 0 && (
                <FormField label="Quick-fill description">
                  <select
                    onChange={(e) => e.target.value && setDescription(e.target.value)}
                    className="w-full h-10 px-space-3 border border-border-light rounded-md font-body text-small bg-white-pure"
                    defaultValue=""
                  >
                    <option value="">Pick a suggested description…</option>
                    {selectedPool.suggestedDescriptions
                      .filter((s) => s.isActive)
                      .sort((a, b) => a.sortOrder - b.sortOrder)
                      .map((s) => (
                        <option key={s.id} value={s.description}>
                          {s.description}
                        </option>
                      ))}
                  </select>
                </FormField>
              )}
            </div>
          )}

          <FormField
            label="Service description"
            required
            helperText="At least 10 characters. Explain what you did that earned this commission."
          >
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="w-full px-space-3 py-space-2 border border-border-light rounded-md font-body text-small bg-white-pure focus:border-gold focus:outline-none"
              placeholder="Sourced and qualified broker introduction, led deal through closing…"
            />
          </FormField>

          <div className="grid grid-cols-2 gap-space-3">
            <FormField label="Gross revenue" required>
              <Input
                type="number"
                step="0.01"
                min={0}
                value={grossRevenue}
                onChange={(e) => setGrossRevenue(e.target.value)}
                placeholder="0.00"
              />
            </FormField>
            <FormField label="Commission percent" required helperText="0.01 – 100.00">
              <Input
                type="number"
                step="0.01"
                min={0}
                max={100}
                value={commissionPercent}
                onChange={(e) => setCommissionPercent(e.target.value)}
                placeholder="10.00"
              />
            </FormField>
          </div>

          <div className="flex items-center justify-between p-space-3 border-t border-border-light">
            <div>
              <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow">
                Commission amount
              </div>
              <div className="text-caption text-gray-mid">
                Auto-calculated on the server from gross × percent ÷ 100.
              </div>
            </div>
            <div className="font-display font-bold text-h2 text-onyx tabular-nums">
              {currency(computedAmount)}
            </div>
          </div>

          {revising && (
            <FormField label="Revision summary" required helperText="What did you change and why?">
              <textarea
                value={revisionSummary}
                onChange={(e) => setRevisionSummary(e.target.value)}
                rows={2}
                className="w-full px-space-3 py-space-2 border border-border-light rounded-md font-body text-small bg-white-pure focus:border-gold focus:outline-none"
              />
            </FormField>
          )}

          {/* A revision is ONE action: `revise` saves the edits and moves the
              claim back to Under review by itself. The save/submit pair below
              is the draft path — offering it here would show a claimant two
              buttons, one of which cannot work (SubmitAsync takes Draft only,
              and updateClaim is ADMIN-gated).

              The third case is the one the widened route created: a claimant
              deep-linking to /edit on a claim of theirs that is NOT contested.
              The draft path is the only thing left, and every button on it
              calls the ADMIN-gated updateClaim, so rendering them would be
              offering controls that cannot do what they appear to. Say so
              instead. */}
          {/* `existing.data` guard, not just `editing`: `revising` is derived
              from the fetched status, so while the claim query is in flight it
              is false — and without this the page would spend the load window
              telling a claimant their contested claim cannot be revised, then
              flip. Say nothing until the status is known. */}
          {editing && existing.data && !revising && !canManualCreate && (
            <div className="flex items-start gap-space-2 p-space-3 border border-border-light rounded-md bg-white-warm text-small text-gray-mid">
              <Info size={16} strokeWidth={1.5} className="text-gold-shadow shrink-0 mt-[2px]" aria-hidden />
              <span>
                This claim isn't open for edits right now. A claim can be revised once Accounting
                contests it — you'll get a notification, and the Revise action appears on the claim
                itself.
              </span>
            </div>
          )}

          <div className="flex gap-space-2 justify-end pt-space-2">
            {revising ? (
              <Button
                variant="primary"
                disabled={reviseDisabled || save.isPending}
                loading={save.isPending}
                onClick={() => {
                  setError(null);
                  save.mutate();
                }}
              >
                Submit revision
              </Button>
            ) : !editing || canManualCreate ? (
              <>
                <Button
                  variant="secondary"
                  disabled={disabled || save.isPending}
                  loading={save.isPending}
                  onClick={() => {
                    setError(null);
                    save.mutate();
                  }}
                >
                  Save draft
                </Button>
                <Button
                  variant="primary"
                  disabled={disabled || submitAfterSave.isPending}
                  loading={submitAfterSave.isPending}
                  onClick={() => {
                    setError(null);
                    submitAfterSave.mutate();
                  }}
                >
                  Submit for review
                </Button>
              </>
            ) : null}
          </div>
        </Card>

        <Card dark>
          <div className="font-display text-label font-semibold uppercase tracking-label text-gold mb-space-2">
            Similar claims
          </div>
          {!poolId ? (
            <div className="text-small text-white-pure/60">Pick a pool to see reference claims.</div>
          ) : similar.isLoading ? (
            <div className="text-small text-white-pure/60">Loading…</div>
          ) : !similar.data || similar.data.length === 0 ? (
            <div className="text-small text-white-pure/60">
              No prior agreed claims in this pool yet — you're charting new ground.
            </div>
          ) : (
            <ul className="flex flex-col gap-space-3 m-0 p-0 list-none">
              {similar.data.map((c) => (
                <li key={c.id} className="border-b border-border-dark pb-space-3 last:border-0">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-caption text-gold-shadow">{c.claimNumber}</span>
                    <ClaimStatusBadge status={c.status} />
                  </div>
                  <div className="flex justify-between text-small mt-space-1">
                    <span className="text-white-pure/60">Gross</span>
                    <span className="tabular-nums">{currency(c.grossRevenue)}</span>
                  </div>
                  <div className="flex justify-between text-small">
                    <span className="text-white-pure/60">Rate</span>
                    <span className="tabular-nums">{percent(c.commissionPercent)}</span>
                  </div>
                  <div className="flex justify-between text-small font-semibold">
                    <span className="text-white-pure/60">Commission</span>
                    <span className="tabular-nums">{currency(c.commissionAmount)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
